import { Box, Paper, ScrollArea, Stack, Text } from '@mantine/core';
import { Resource } from '@medplum/fhirtypes';
import { useTranslation } from 'react-i18next';
import { ResourceListSectionConfig } from './ResourceListSectionConfig';

// -----------------------------------------------------------------------------
export interface ResourceListDisplayProps<T extends Resource> {
  readonly resources: T[];
  readonly config: ResourceListSectionConfig<T>;
}

// Maximum number of items to show before enabling scroll
const MAX_VISIBLE_ITEMS = 3;

// Maximum height for scrollable area (60vh = 60% of viewport height)
const MAX_SCROLL_HEIGHT = '60vh';

// -----------------------------------------------------------------------------
export const ResourceListDisplay = <T extends Resource>({
  resources,
  config,
}: ResourceListDisplayProps<T>) => {
  const { t } = useTranslation(['patient-pages']);

  // ---------------------------------------------------------------------------
  if (!resources?.length) {
    return (
      <Paper p="md" radius="md" withBorder bg="gray.0">
        <Text c="dimmed" ta="center" fs="italic" fz="sm">
          {t(config.noResourcesKey, {
            ns: 'patient-pages',
            defaultValue: 'No records found',
          })}
        </Text>
      </Paper>
    );
  }

  // ---------------------------------------------------------------------------
  const content = (
    <Stack gap="md">
      {resources.map((resource) => (
        <Box key={resource.id}>{config.renderItem(resource)}</Box>
      ))}
    </Stack>
  );

  // ---------------------------------------------------------------------------
  // If more than MAX_VISIBLE_ITEMS, enable scrolling with max height
  if (resources.length > MAX_VISIBLE_ITEMS) {
    return (
      <ScrollArea h={MAX_SCROLL_HEIGHT} type="scroll" offsetScrollbars>
        {content}
      </ScrollArea>
    );
  }

  // ---------------------------------------------------------------------------
  return content;
};
